import { Controller } from "@hotwired/stimulus"

/**
 * JAP1 Flashcard Controller
 * Handles the flashcard interface for learning questions chapter by chapter
 */
export default class extends Controller {
  static targets = [
    "card",
    "questionSide",
    "answerSide",
    "questionNumber",
    "questionText",
    "answerText",
    "revealBtn",
    "prevBtn",
    "nextBtn",
    "progressBar",
    "currentIndex",
    "totalQuestions",
    "quickNav",
    "navBtn"
  ]

  static values = {
    chapitre: Number,
    questions: Array,
    current: { type: Number, default: 0 }
  }

  connect() {
    this.loadQuestions()
    this.displayCurrentQuestion()
    this.updateNavigation()
    this.loadProgressFromStorage()

    // Keyboard navigation
    document.addEventListener("keydown", this.handleKeydown.bind(this))
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeydown.bind(this))
  }

  // Load questions from value or default
  loadQuestions() {
    if (!this.questionsValue || this.questionsValue.length === 0) {
      this.questionsValue = []
    }

    if (this.hasTotalQuestionsTarget) {
      this.totalQuestionsTarget.textContent = this.questionsValue.length
    }
  }

  // Display the current question
  displayCurrentQuestion() {
    if (this.questionsValue.length === 0) return

    const question = this.questionsValue[this.currentValue]
    if (!question) return

    // Update question number
    if (this.hasQuestionNumberTarget) {
      this.questionNumberTarget.textContent = `Question ${this.currentValue + 1}`
    }

    // Update current index
    if (this.hasCurrentIndexTarget) {
      this.currentIndexTarget.textContent = this.currentValue + 1
    }

    // Update question text
    if (this.hasQuestionTextTarget) {
      this.questionTextTarget.textContent = question.question || "Question non disponible"
    }

    // Update answer text
    if (this.hasAnswerTextTarget) {
      this.answerTextTarget.textContent = question.reponse || "Réponse non disponible"
    }

    // Reset to question side
    this.hideAnswer()

    // Update progress bar
    this.updateProgressBar()

    // Mark as seen in localStorage
    this.markQuestionAsSeen()
  }

  // Reveal the answer
  reveal() {
    if (this.hasQuestionSideTarget && this.hasAnswerSideTarget) {
      this.answerSideTarget.classList.remove("hidden")
    }
    if (this.hasRevealBtnTarget) {
      this.revealBtnTarget.classList.add("hidden")
    }
  }

  // Hide the answer
  hideAnswer() {
    if (this.hasAnswerSideTarget) {
      this.answerSideTarget.classList.add("hidden")
    }
    if (this.hasRevealBtnTarget) {
      this.revealBtnTarget.classList.remove("hidden")
    }
  }

  // Go to next question
  next() {
    if (this.currentValue < this.questionsValue.length - 1) {
      this.currentValue++
      this.displayCurrentQuestion()
      this.updateNavigation()
    }
  }

  // Go to previous question
  previous() {
    if (this.currentValue > 0) {
      this.currentValue--
      this.displayCurrentQuestion()
      this.updateNavigation()
    }
  }

  // Go to specific question
  goTo(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10)
    if (!isNaN(index) && index >= 0 && index < this.questionsValue.length) {
      this.currentValue = index
      this.displayCurrentQuestion()
      this.updateNavigation()
    }
  }

  // Mark question with a level (review, medium, mastered)
  markAs(event) {
    const level = event.currentTarget.dataset.level
    const chapitreId = this.chapitreValue
    const questionIndex = this.currentValue

    // Get progress controller if available
    const progressController = this.getProgressController()

    if (progressController) {
      if (level === "mastered") {
        progressController.markAsMastered(chapitreId, questionIndex)
      } else if (level === "review") {
        progressController.markForReview(chapitreId, questionIndex)
      } else if (level === "medium") {
        // Mark as seen but not mastered
        progressController.markAsSeen(chapitreId, questionIndex)
      }
    }

    // Update nav button style
    this.updateNavButtonStyle(questionIndex, level)

    // Auto advance to next question
    setTimeout(() => this.next(), 300)
  }

  // Update navigation buttons state
  updateNavigation() {
    if (this.hasPrevBtnTarget) {
      this.prevBtnTarget.disabled = this.currentValue === 0
    }
    if (this.hasNextBtnTarget) {
      this.nextBtnTarget.disabled = this.currentValue >= this.questionsValue.length - 1
    }

    // Update quick nav active state
    this.navBtnTargets.forEach((btn, index) => {
      btn.classList.toggle("bg-[#0055A4]", index === this.currentValue)
      btn.classList.toggle("text-white", index === this.currentValue)
      btn.classList.toggle("border-[#0055A4]", index === this.currentValue)
    })
  }

  // Update progress bar
  updateProgressBar() {
    if (this.hasProgressBarTarget && this.questionsValue.length > 0) {
      const percent = ((this.currentValue + 1) / this.questionsValue.length) * 100
      this.progressBarTarget.style.width = `${percent}%`
    }
  }

  // Update nav button style based on mastery level
  updateNavButtonStyle(index, level) {
    const btn = this.navBtnTargets[index]
    if (!btn) return

    // Reset classes
    btn.classList.remove("bg-green-500", "bg-red-500", "bg-yellow-500", "text-white")

    if (level === "mastered") {
      btn.classList.add("bg-green-500", "text-white")
    } else if (level === "review") {
      btn.classList.add("bg-red-500", "text-white")
    } else if (level === "medium") {
      btn.classList.add("bg-yellow-500", "text-white")
    }
  }

  // Mark question as seen in localStorage
  markQuestionAsSeen() {
    const progressController = this.getProgressController()
    if (progressController) {
      progressController.markAsSeen(this.chapitreValue, this.currentValue)
    }
  }

  // Load progress from storage and update nav buttons
  loadProgressFromStorage() {
    const progressController = this.getProgressController()
    if (!progressController) return

    const chapitre = progressController.getChapitreProgress(this.chapitreValue)

    // Update nav buttons based on saved progress
    this.navBtnTargets.forEach((btn, index) => {
      if (chapitre.mastered?.includes(index)) {
        btn.classList.add("bg-green-500", "text-white")
      }
    })

    // Also check toReview
    const toReview = progressController.progress?.toReview || []
    toReview.forEach(item => {
      if (item.chapitreId === this.chapitreValue) {
        const btn = this.navBtnTargets[item.questionIndex]
        if (btn) {
          btn.classList.add("bg-red-500", "text-white")
        }
      }
    })
  }

  // Get the progress controller from the page
  getProgressController() {
    const element = document.querySelector("[data-controller~='jap1-progress']")
    if (element) {
      return this.application.getControllerForElementAndIdentifier(element, "jap1-progress")
    }
    return null
  }

  // Handle keyboard navigation
  handleKeydown(event) {
    // Don't handle if user is typing in an input
    if (event.target.tagName === "INPUT" || event.target.tagName === "TEXTAREA") {
      return
    }

    switch (event.key) {
      case "ArrowRight":
      case "n":
        event.preventDefault()
        this.next()
        break
      case "ArrowLeft":
      case "p":
        event.preventDefault()
        this.previous()
        break
      case " ":
      case "Enter":
        event.preventDefault()
        if (this.hasAnswerSideTarget && this.answerSideTarget.classList.contains("hidden")) {
          this.reveal()
        } else {
          this.next()
        }
        break
      case "1":
        event.preventDefault()
        this.markAs({ currentTarget: { dataset: { level: "review" } } })
        break
      case "2":
        event.preventDefault()
        this.markAs({ currentTarget: { dataset: { level: "medium" } } })
        break
      case "3":
        event.preventDefault()
        this.markAs({ currentTarget: { dataset: { level: "mastered" } } })
        break
    }
  }

  // Shuffle questions
  shuffle() {
    for (let i = this.questionsValue.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [this.questionsValue[i], this.questionsValue[j]] = [this.questionsValue[j], this.questionsValue[i]]
    }
    this.currentValue = 0
    this.displayCurrentQuestion()
    this.updateNavigation()
  }
}
